import { motion } from "framer-motion";
import { Check, Loader2, ShieldCheck, MonitorSmartphone } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { GuaranteeBadge } from "./GuaranteeBadge";

export const Consulting = ({ prices, onBuy, buyingKey }) => {
  const { content } = useContent();
  const c = content.consulting || {};
  const tiers = c.tiers || [];
  const credentials = c.credentials || [];

  return (
    <section id="one-on-one" className="relative py-24 lg:py-36 max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-6">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">// Done-With-You</span>
          <h2 className="mt-5 font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl leading-[0.9]">{c.heading}</h2>
          <p className="mt-6 font-body text-white/55 leading-relaxed">{c.sub}</p>
        </div>
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/40 flex-shrink-0">
          <MonitorSmartphone size={16} className="text-accent" /> Phone · PC · Screenshots welcome
        </div>
      </div>

      {/* Credentials */}
      <div className="flex flex-wrap gap-2 mb-14">
        {credentials.map((cred) => (
          <span key={cred} className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.03] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/60">
            <ShieldCheck size={12} className="text-accent" /> {cred}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {tiers.map((t, i) => {
          const price = prices?.[t.lookup_key];
          const isFeatured = !!t.featured;
          return (
            <motion.div
              key={t.lookup_key}
              data-testid={`consult-tier-${t.lookup_key}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`relative p-8 lg:p-9 flex flex-col ${isFeatured ? "bg-[#0d0d0d]" : "bg-[#050505]"}`}
            >
              {isFeatured && <span className="absolute top-0 right-0 bg-accent text-white font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1.5">Most Popular</span>}
              <h3 className="font-display font-black uppercase tracking-tight text-2xl">{t.name}</h3>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-accent">{t.duration}</p>
              <div className="mt-6 flex items-baseline gap-2">
                <span data-testid={`consult-price-${t.lookup_key}`} className="font-display font-black text-4xl">{price || "-"}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">one-time</span>
              </div>
              <p className="mt-4 font-body text-sm text-white/55 leading-relaxed">{t.desc}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {(t.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check size={14} className="text-accent mt-0.5 flex-shrink-0" strokeWidth={3} />
                    <span className="font-body text-sm text-white/70">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`consult-buy-${t.lookup_key}`}
                onClick={() => onBuy(t.lookup_key)}
                disabled={buyingKey === t.lookup_key}
                className={`group relative mt-8 inline-flex items-center justify-center gap-2 font-mono uppercase tracking-[0.15em] text-xs px-6 py-4 overflow-hidden disabled:opacity-70 ${isFeatured ? "bg-accent text-white" : "border border-white/20 text-white"}`}
              >
                <span className={`absolute inset-0 ${isFeatured ? "bg-white" : "bg-accent"} translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]`} />
                <span className={`relative z-10 flex items-center gap-2 transition-colors duration-300 ${isFeatured ? "group-hover:text-black" : "group-hover:text-white"}`}>
                  {buyingKey === t.lookup_key ? <><Loader2 size={14} className="animate-spin" /> Opening…</> : "Book This Session"}
                </span>
              </button>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-white/40 max-w-xl">{c.note}</p>
        <GuaranteeBadge />
      </div>
    </section>
  );
};
